/**
 * The curated brand-accent palette and every rule the colour swatch needs. React-free by mandate
 * (epic #26), exactly like its sibling {@link ./brandColors}.
 *
 * Since ui#622 this palette is the ONLY way a sender picks an accent — the free-form native colour
 * input is gone (owner decision, 2026-08-08). ui#1923 then replaced the animated radial disc that
 * presented it with a plain wrapped row of circles (owner, 2026-09-03: "palette disc can be a simple
 * swatch"), which is why the geometry, the pointer repulsion and the conic rim this module used to own
 * are gone: with no disc to lay out, they had no caller and nothing left to be true about.
 *
 * What remains is the data plus the rules a view would otherwise inline, and every one of them takes
 * the OFFERED LIST first, because ui#1923 also made that list a prop:
 *
 *   1. {@link paletteSelectionOf} — which swatch, if any, a SAVED colour corresponds to.
 *   2. {@link paletteSelectedStop} — the same answer widened to include the clear stop.
 *   3. {@link paletteTabStop} — which stop holds the radiogroup's single tab stop.
 *   4. {@link paletteNavigate} / {@link paletteNavigateFrom} — where an arrow key moves.
 *
 * Since ui#2416 a surface may also offer {@link PALETTE_NO_COLOR}, a "no colour" stop that sits FIRST
 * in the row (owner 2026-09-12, replacing the personalization builder's "Clear colour" pill). It is a
 * full member of the group — selectable, tabbable, walked by the arrow keys — so every rule below takes
 * a trailing `clearable`. It defaults to `false` because the account brand sheet and the sponsor colour
 * field have no such state: their accent is always set, and on those surfaces these rules answer exactly
 * what they answered before the stop existed.
 *
 * Every colour here is a strict lowercase `#rrggbb` that {@link ./brandColors.normalizeBrandColor}
 * accepts — `brandPalette.test.ts` ("ships only colours the api will accept, already canonical")
 * keeps it that way, because an upper-case entry would save fine and then never match itself in
 * {@link paletteSelectionOf}. No colour here is vetted for contrast, and each surface is readable for its
 * own reason, so the palette is free to span the whole hue wheel. An account ACCENT is painted as a
 * background, and the api derives the readable foreground for whatever it is given (see the WCAG note in
 * `brandColors.ts`). The poster's {@link POSTER_COLOR_PRESETS} are painted as TEXT, where nothing is
 * derived and a pair can clash (emerald on white is 2.85:1, white on white 1:1): there the shared renderer
 * moves a failing text colour toward black or white until it reads at 4.5:1 (`posterContrast.ts`'s
 * `posterPaint`, kitecorp/transferify-ui#2416).
 */

import {isBrandColor, normalizeBrandColor} from "./brandColors";

/**
 * A stable per-colour key. Doubles as the i18n key for the swatch's accessible name.
 *
 * The last two, `black` and `white`, are offered ONLY by the wallpaper poster's rows
 * ({@link POSTER_COLOR_PRESETS}, ui#2416) and are not account accents.
 */
export type BrandPaletteColorId =
	| "navy" | "forest" | "wine" | "plum" | "slate" | "espresso"
	| "cobalt" | "sky" | "cyan" | "emerald" | "olive" | "amber"
	| "orange" | "scarlet" | "ruby" | "fuchsia" | "violet" | "indigo"
	| "cornflower" | "azure" | "aqua" | "turquoise" | "mint" | "springGreen"
	| "lime" | "yellow" | "gold" | "apricot" | "salmon" | "coral"
	| "pink" | "orchid" | "lavender" | "periwinkle" | "iris" | "mist"
	| "black" | "white";

export interface BrandPaletteEntry {
	readonly id: BrandPaletteColorId;
	/** Canonical `#rrggbb`, lowercase — the exact value sent to `SetBrandColor`. */
	readonly hex: string;
}

/**
 * The "no colour" stop's id (ui#2416) — not a colour, and deliberately not spellable as one: no entry
 * in {@link BRAND_PALETTE} may ever take this name, which `brandPalette.test.ts` asserts.
 */
export const PALETTE_NO_COLOR = "none" as const;

/** Everything the group can put focus on: a colour, or the clear stop where a surface offers one. */
export type PaletteStop = BrandPaletteColorId | typeof PALETTE_NO_COLOR;

/**
 * The palette in three tone bands, and the bands in this order, because ORDER is now the whole layout:
 * a wrapped row reads left to right, so the six deep anchors come first, then the saturated core, then
 * the light tones. Within a band the colours walk the hue wheel, which is what stops the row reading as
 * an unsorted spill of 36 circles.
 *
 * The bands are also the keyboard order, since {@link paletteNavigate} walks this flat list.
 */
const TONE_BANDS: readonly (readonly (readonly [BrandPaletteColorId, string])[])[] = [
	[
		["navy", "#0b3d91"],
		["slate", "#1f2937"],
		["forest", "#0f5132"],
		["espresso", "#7c2d12"],
		["wine", "#7a1220"],
		["plum", "#4c1d95"],
	],
	[
		["cobalt", "#2b50e0"],
		["sky", "#0ea5e9"],
		["cyan", "#06b6d4"],
		["emerald", "#0fae7e"],
		["olive", "#65a30d"],
		["amber", "#ca8a04"],
		["orange", "#ea580c"],
		["scarlet", "#dc2626"],
		["ruby", "#e11d48"],
		["fuchsia", "#c026d3"],
		["violet", "#7c3aed"],
		["indigo", "#4f46e5"],
	],
	[
		["cornflower", "#60a5fa"],
		["azure", "#38bdf8"],
		["aqua", "#22d3ee"],
		["turquoise", "#2dd4bf"],
		["mint", "#34d399"],
		["springGreen", "#4ade80"],
		["lime", "#a3e635"],
		["yellow", "#facc15"],
		["gold", "#fbbf24"],
		["apricot", "#fb923c"],
		["salmon", "#f87171"],
		["coral", "#fb7185"],
		["pink", "#f472b6"],
		["orchid", "#e879f9"],
		["lavender", "#c084fc"],
		["periwinkle", "#a78bfa"],
		["iris", "#818cf8"],
		["mist", "#94a3b8"],
	],
];

/** The whole palette, flat. What the account brand sheet offers. */
export const BRAND_PALETTE: readonly BrandPaletteEntry[] =
	TONE_BANDS.flatMap(band => band.map(([id, hex]) => ({id, hex})));

/**
 * Every colour ANY surface offers: the 36 account accents plus the poster's black and white (ui#2416).
 *
 * The list the accessible names are checked against (`brandPaletteCopy.test.ts`) and the list ids resolve
 * through, so a colour offered anywhere has a name everywhere. The two neutrals stay out of
 * {@link BRAND_PALETTE} on purpose: a white accent on a white recipient page is something the account
 * brand sheet never offered, and the poster adding the pair is no reason for that surface to change.
 */
export const NAMED_COLORS: readonly BrandPaletteEntry[] = [
	...BRAND_PALETTE,
	{id: "black", hex: "#000000"},
	{id: "white", hex: "#ffffff"},
];

const BY_ID = new Map<BrandPaletteColorId, BrandPaletteEntry>(NAMED_COLORS.map(entry => [entry.id, entry]));

export function paletteEntry(id: BrandPaletteColorId): BrandPaletteEntry {
	const entry = BY_ID.get(id);
	if (!entry) {
		throw new Error(`Unknown palette colour: ${id}`);
	}
	return entry;
}

/**
 * The five the personalization builder offers (owner, 2026-09-03: "5 is fine").
 *
 * Drawn FROM {@link BRAND_PALETTE} rather than added beside it, so a preset is always a colour the
 * account brand sheet can express too and no new hex enters the product through the builder. Blue,
 * green, yellow, rose and the palette's one dark neutral — the five families the owner-approved
 * prototype shows, each mapped onto the nearest colour that already exists here.
 */
export const PALETTE_PRESETS: readonly BrandPaletteEntry[] =
	(["cobalt", "emerald", "gold", "ruby", "slate"] as const).map(paletteEntry);

/**
 * The five the wallpaper poster's TEXT COLOUR and TEXT BACKGROUND rows both offer (ui#2416; owner
 * 2026-09-12/13: a small set of swatches, defaulting to black text on a white background).
 *
 * Black and white first, because they are the two defaults and the pair every other choice is read
 * against. The three hues are the builder's own blue, green and rose presets, so no new hue enters the
 * product through the poster. One list for both rows rather than one each: the rows choose two colours
 * for one pair, and a colour that could be a background but never words (or the reverse) would be a
 * rule nobody could see from the swatches.
 */
export const POSTER_COLOR_PRESETS: readonly BrandPaletteEntry[] =
	(["black", "white", "cobalt", "emerald", "ruby"] as const).map(paletteEntry);

/**
 * The swatch a saved accent corresponds to WITHIN THE OFFERED LIST, or `null` when it is not one of
 * them.
 *
 * `null` is a first-class answer, not a failure, for two real states. Accounts branded before ui#622
 * hold any colour the old free-form input allowed, and the control MUST show no swatch as chosen for
 * them rather than rounding to the nearest one. And since ui#1923 the builder offers five of the 36,
 * so a sender whose accent is a palette colour the builder leaves out is in the same position. Both
 * still see their real colour in the preview beside the control, and the account brand sheet also
 * prints it in a hex readout. The poster's colour rows have no readout, so a stored colour outside their
 * five (a page theme adopted from the retired Light/Dark tone) is visible only in the poster preview.
 */
export function paletteSelectionOf(
	colors: readonly BrandPaletteEntry[],
	color: string | null | undefined,
): BrandPaletteColorId | null {
	if (!isBrandColor(color)) {
		return null;
	}
	const normalized = normalizeBrandColor(color as string);
	return colors.find(entry => entry.hex === normalized)?.id ?? null;
}

/**
 * The same answer as {@link paletteSelectionOf}, widened to the clear stop.
 *
 * The clear stop is chosen only when there is NO COLOUR AT ALL. A saved accent the offered list leaves
 * out still marks nothing (see {@link paletteSelectionOf}): marking "no colour" there would tell a
 * sender their page has no accent while the preview beside the row paints one.
 */
export function paletteSelectedStop(
	colors: readonly BrandPaletteEntry[],
	color: string | null | undefined,
	clearable = false,
): PaletteStop | null {
	const selected = paletteSelectionOf(colors, color);
	if (selected !== null) {
		return selected;
	}
	return clearable && !isBrandColor(color) ? PALETTE_NO_COLOR : null;
}

/**
 * Every stop the group holds, in the order they are rendered — the clear stop first, then the colours.
 *
 * One list feeds the tab stop and both navigation rules, so "is the clear swatch a stop here" has a
 * single answer rather than one per rule.
 */
function paletteStops(colors: readonly BrandPaletteEntry[], clearable: boolean): readonly PaletteStop[] {
	const ids = colors.map(entry => entry.id);
	return clearable ? [PALETTE_NO_COLOR, ...ids] : ids;
}

/**
 * The single stop that takes the group's tab stop (the roving-tabindex pattern for `radiogroup`).
 *
 * Falls back to the FIRST stop whenever the selection is absent or is not on offer, so both states stay
 * reachable by keyboard — a group where every option is `tabindex="-1"` cannot be tabbed into at all.
 * With a clear stop that first stop IS the clear swatch, which is what gives an unbranded transfer a
 * tab stop that is also the chosen one. `null` only for an empty group, which has nothing to focus.
 */
export function paletteTabStop(
	colors: readonly BrandPaletteEntry[],
	selected: PaletteStop | null,
	clearable = false,
): PaletteStop | null {
	const stops = paletteStops(colors, clearable);
	if (selected !== null && stops.includes(selected)) {
		return selected;
	}
	return stops.length === 0 ? null : stops[0];
}

/** The keys {@link paletteNavigate} answers to. Anything else leaves focus where it is. */
export type PaletteNavigationKey =
	"ArrowRight" | "ArrowLeft" | "ArrowDown" | "ArrowUp" | "Home" | "End";

const NAVIGATION_KEYS: readonly string[] = ["ArrowRight", "ArrowLeft", "ArrowDown", "ArrowUp", "Home", "End"];

export function isPaletteNavigationKey(key: string): key is PaletteNavigationKey {
	return NAVIGATION_KEYS.includes(key);
}

/**
 * Where an arrow key moves, given where it is now.
 *
 * BOTH axes walk the flat list, which is the W3C radio-group pattern and the only honest rule here:
 * the swatch row WRAPS, and where it wraps depends on the width the browser hands it, so a rule that
 * claimed to move "one row down" would be guessing at a layout this module cannot see. Home and End go
 * to the ends of the offered list. Every direction wraps, so focus can never fall out of the group.
 *
 * A colour that is not on offer has no neighbours in the list, so the walk starts at the first stop
 * rather than throwing — that is the out-of-palette account pressing an arrow key.
 */
export function paletteNavigate(
	colors: readonly BrandPaletteEntry[],
	from: PaletteStop,
	key: PaletteNavigationKey,
	clearable = false,
): PaletteStop | null {
	const stops = paletteStops(colors, clearable);
	if (stops.length === 0) {
		return null;
	}
	const at = stops.indexOf(from);
	if (at < 0) {
		return stops[0];
	}
	switch (key) {
		case "ArrowRight":
		case "ArrowDown":
			return stops[(at + 1) % stops.length];
		case "ArrowLeft":
		case "ArrowUp":
			return stops[(at - 1 + stops.length) % stops.length];
		case "Home":
			return stops[0];
		case "End":
			return stops[stops.length - 1];
	}
}

/**
 * Where an arrow key moves when the group may have NO selection yet — the entry point the view calls.
 *
 * An unselected group commits to its tab stop before it starts moving. Delegating straight to
 * {@link paletteNavigate} instead would make the very first arrow press skip the swatch the sender's
 * focus ring is sitting on, which looks like a dropped keystroke. This is the out-of-palette account's
 * first keyboard interaction, so it is the one that has to feel right.
 *
 * EXCEPT WITH A CLEAR STOP. There, nothing is selected only while a colour OFF the list is set (no colour
 * at all selects the clear stop), and the tab stop is the clear swatch. Committing to it would REMOVE that
 * colour on a key that was only meant to move: a page theme adopted from the retired dark tone, pressing
 * right to reach Black, would lose its colour instead (review of kitecorp/transferify-ui#2418). So the walk
 * starts FROM the clear stop, and only Home, which names it, lands on it.
 */
export function paletteNavigateFrom(
	colors: readonly BrandPaletteEntry[],
	selected: PaletteStop | null,
	key: PaletteNavigationKey,
	clearable = false,
): PaletteStop | null {
	if (selected !== null) {
		return paletteNavigate(colors, selected, key, clearable);
	}
	const tabStop = paletteTabStop(colors, null, clearable);
	return clearable && tabStop !== null ? paletteNavigate(colors, tabStop, key, clearable) : tabStop;
}
